import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { tenantService } from '../services/tenantService';
import { propertyService } from '../services/propertyService';

const TenantPropertyPage = () => {
  const { currentUser, userData } = useAuth();
  const [tenantAssignments, setTenantAssignments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser && userData) {
      const unsubscribeFunctions = [];

      // Subscribe to tenant assignments
      const assignmentUnsubscribe = tenantService.subscribeToTenantAssignmentsByTenant(
        currentUser.uid,
        (assignments) => {
          setTenantAssignments(assignments || []);
        }
      );
      unsubscribeFunctions.push(assignmentUnsubscribe);

      // Subscribe to properties
      const propertyUnsubscribe = propertyService.subscribeToPropertiesByTenant(
        currentUser.uid,
        (properties) => {
          setProperties(properties || []);
          setLoading(false);
        }
      );
      unsubscribeFunctions.push(propertyUnsubscribe);

      return () => {
        unsubscribeFunctions.forEach(unsubscribe => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      };
    } else {
      setLoading(false);
    }
  }, [currentUser, userData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property information...</p>
        </div>
      </div>
    );
  }

  const currentAssignment = tenantAssignments[0];
  const currentProperty = properties[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64">
          <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="px-8 py-6">
              <h1 className="text-2xl font-bold text-gray-800">My Property</h1>
            </div>
          </header>

          <div className="p-8">
            {!currentProperty ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">No property assigned</h3>
                <p className="text-gray-600">You haven't been assigned to any property yet.</p>
              </div>
            ) : (
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Property Details */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Property Details</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-800 mb-2">{currentProperty.name}</h3>
                      <p className="text-gray-600 mb-4">{currentProperty.address}</p>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Property Type:</span>
                          <span className="text-sm font-medium text-gray-800 capitalize">{currentProperty.type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Status:</span>
                          <span className="text-sm font-medium text-gray-800 capitalize">{currentProperty.status}</span>
                        </div>
                        {currentProperty.bedrooms && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Bedrooms:</span>
                            <span className="text-sm font-medium text-gray-800">{currentProperty.bedrooms}</span>
                          </div>
                        )}
                        {currentProperty.bathrooms && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Bathrooms:</span>
                            <span className="text-sm font-medium text-gray-800">{currentProperty.bathrooms}</span>
                          </div>
                        )}
                        {currentProperty.squareFootage && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Square Feet:</span>
                            <span className="text-sm font-medium text-gray-800">{currentProperty.squareFootage.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lease Information */}
                {currentAssignment && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Lease Information</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Lease Period</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Start Date:</span>
                            <span className="text-sm font-medium text-gray-800">
                              {new Date(currentAssignment.leaseStartDate).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">End Date:</span>
                            <span className="text-sm font-medium text-gray-800">
                              {new Date(currentAssignment.leaseEndDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Financial Details</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Monthly Rent:</span>
                            <span className="text-sm font-medium text-gray-800">
                              ${currentAssignment.monthlyRent?.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Lease Status:</span>
                            <span className="text-sm font-medium text-gray-800 capitalize">
                              {currentAssignment.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantPropertyPage; 